import { uiText } from '../i18n/uiText';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useDraftBuffer from '../hooks/useDraftBuffer';
import FormDraftNotice from '../components/common/FormDraftNotice';
import { Link, useParams } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import PageIntro, { HelpText, QuickActions } from '../components/ui/PageIntro';
import Badge from '../components/ui/Badge';
import { selfCheckApi, getApiErrorMessage } from '../api';
import { formatDate } from '../utils/formatters';
import { useAuth } from '../store/AuthContext';

const STATUS_META = {
  draft: { label: uiText("Черновик"), variant: 'neutral' },
  submitted: { label: uiText("Ожидает проверки"), variant: 'warning' },
  reviewed: { label: uiText("Проверен"), variant: 'success' },
};

const RATING_META = {
  green: { label: uiText("Зелёный"), variant: 'success' },
  yellow: { label: uiText("Жёлтый"), variant: 'warning' },
  red: { label: uiText("Красный"), variant: 'danger' },
};

const BLOCK_LABELS = {
  legal: uiText("Юридическая состоятельность"),
  financial: uiText("Финансовая устойчивость"),
  reputation: uiText("Репутация и опыт"),
};

function answerLabel(answer) {
  const value = answer && typeof answer === 'object' ? answer.value : answer;
  if (value === true) return uiText("Да");
  if (value === false) return uiText("Нет");
  return uiText("Не заполнено");
}

function answerVariant(answer) {
  const value = answer && typeof answer === 'object' ? answer.value : answer;
  if (value === true) return 'success';
  if (value === false) return 'danger';
  return 'neutral';
}

export default function SelfCheckReviewPage() {
  const { companyId } = useParams();
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [check, setCheck] = useState(null);
  const buffer = useDraftBuffer(user?.id, check?.id ?? `loading-${companyId}`, 'self-check-review');
  const notes = buffer.values.notes ?? check?.notes ?? '';
  const setNotes = value => { if (!busy && !buffer.recovery) buffer.edit('notes', value); };
  useEffect(() => { buffer.hydrate({ notes: check?.notes || '' }); }, [check, buffer]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const canReview = ['admin', 'auditor'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [definitionRes, checkRes] = await Promise.all([
        selfCheckApi.definition(),
        selfCheckApi.getCompany(companyId),
      ]);
      setQuestions(definitionRes.data?.questions || []);
      setCheck(checkRes.data || null);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const groupedQuestions = useMemo(() => {
    const out = {};
    (questions || []).forEach((question) => {
      const key = question.block || 'other';
      if (!out[key]) out[key] = [];
      out[key].push(question);
    });
    return out;
  }, [questions]);

  const docsByQuestion = useMemo(() => {
    const out = {};
    (check?.documents || []).forEach((doc) => {
      const key = String(doc.checklist_item || '');
      if (!key) return;
      if (!out[key]) out[key] = [];
      out[key].push(doc);
    });
    return out;
  }, [check]);

  const counters = useMemo(() => {
    const answers = check?.answers || {};
    const answered = Object.values(answers).filter((answer) => {
      const value = answer && typeof answer === 'object' ? answer.value : answer;
      return value === true || value === false;
    }).length;
    const yes = Object.values(answers).filter((answer) => (answer && typeof answer === 'object' ? answer.value : answer) === true).length;
    const no = Object.values(answers).filter((answer) => (answer && typeof answer === 'object' ? answer.value : answer) === false).length;
    return { answered, yes, no, documents: check?.documents?.length || 0 };
  }, [check]);

  const review = async () => {
    if (!check?.id || !canReview || busy || buffer.recovery) return;
    const snapshot = buffer.snapshot();
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const res = await selfCheckApi.review(check.id, notes);
      setCheck(res.data);
      buffer.acknowledge(snapshot, { committed: true, values: { notes: res.data?.notes || '' } });
      setSuccess(uiText("Анкета организации отмечена как проверенная"));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const status = STATUS_META[check?.status] || { label: check?.status || uiText("Неизвестно"), variant: 'neutral' };
  const rating = RATING_META[check?.rating_color] || null;
  const companyTitle = check?.company?.name || check?.company?.legal_name || uiText("Компания #{v0}", {v0: (check?.company_id)});
  const companyMeta = [check?.company?.legal_name, check?.company?.inn ? uiText("ИНН {v0}", {v0: (check.company.inn)}) : null].filter(Boolean).join(' · ');

  return (
    <div className="workspace-page">
      {canReview && <FormDraftNotice buffer={buffer} label={uiText("Проверка анкеты организации")} disabled={busy || loading} />}
      <PageIntro
        eyebrow={uiText("Благонадёжность контрагента")}
        title={uiText("Проверка анкеты компании")}
        icon={ClipboardCheck}
        description={uiText("Ответы проверяемой организации, приложенные материалы и отметка аудитора о завершении проверки.")}
        aside={(
          <div className="workspace-side-actions">
            <button type="button" className="btn btn-outline" onClick={load} disabled={loading}>
              <RefreshCw size={15} />{uiText(" Обновить")}</button>
            <Link className="btn btn-outline" to="/notifications">{uiText("К уведомлениям")}</Link>
          </div>
        )}
      >
        <HelpText>{uiText("После проверки рейтинг пересчитывается и сохраняется в карточке компании для дальнейшего аудита и дашборда руководителя.")}</HelpText>
        <QuickActions>
          <Link className="btn btn-outline" to="/companies">{uiText("Компании")}</Link>
          {user?.role === 'admin' ? <Link className="btn btn-outline" to="/admin/self-check-weights">{uiText("Настройки оценки благонадёжности")}</Link> : null}
        </QuickActions>
      </PageIntro>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {success ? <div className="alert alert-success">{success}</div> : null}

      {loading ? (
        <div className="loading-center"><div className="spinner" />{uiText("Загружаем анкету организации…")}</div>
      ) : !check ? (
        <div className="card">
          <div className="card-body workspace-empty-state">
            <div className="workspace-empty-state-title">{uiText("Анкета организации не найдена")}</div>
            <div className="workspace-empty-state-text">{uiText("Компания ещё не отправила анкету или доступ к ней закрыт.")}</div>
          </div>
        </div>
      ) : (
        <>
          <div className="workspace-stats-grid">
            <div className="workspace-stat">
              <div className="workspace-stat-value">{counters.answered}</div>
              <div className="workspace-stat-label">{uiText("Ответов")}</div>
              <div className="workspace-stat-hint">{uiText("Да: ")}{counters.yes}{uiText(" · Нет: ")}{counters.no}</div>
            </div>
            <div className="workspace-stat">
              <div className="workspace-stat-value">{counters.documents}</div>
              <div className="workspace-stat-label">{uiText("Документов")}</div>
              <div className="workspace-stat-hint">{uiText("Приложения к вопросам анкеты")}</div>
            </div>
            <div className="workspace-stat">
              <div className="workspace-stat-value">{check.rating_score ?? '—'}</div>
              <div className="workspace-stat-label">{uiText("Рейтинг")}</div>
              <div className="workspace-stat-hint">{uiText("Баллы после расчёта благонадёжности")}</div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">{uiText("Состояние проверки")}</div>
              <div className="workspace-chip-row">
                <Badge variant={status.variant}>{status.label}</Badge>
                {rating ? <Badge variant={rating.variant}>{rating.label}</Badge> : null}
              </div>
            </div>
            <div className="card-body" style={{ display: 'grid', gap: 14 }}>
              <div className="workspace-link-grid">
                <div className="workspace-link-card">
                  <div className="workspace-section-label">{uiText("Компания")}</div>
                  <div className="workspace-item-title">{companyTitle}</div>
                  <div className="workspace-muted">{companyMeta || uiText("Идентификатор компании #{v0}", {v0: (check.company_id)})}</div>
                </div>
                <div className="workspace-link-card">
                  <div className="workspace-section-label">{uiText("Отправлено")}</div>
                  <div className="workspace-item-title">{formatDate(check.submitted_at, 'datetime') || '—'}</div>
                  <div className="workspace-muted">{uiText("Дата передачи анкеты на проверку")}</div>
                </div>
                <div className="workspace-link-card">
                  <div className="workspace-section-label">{uiText("Проверено")}</div>
                  <div className="workspace-item-title">{formatDate(check.reviewed_at, 'datetime') || '—'}</div>
                  <div className="workspace-muted">{uiText("Дата последней отметки аудитора")}</div>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{uiText("Комментарий проверяющего")}</label>
                <textarea
                  className="form-control"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  disabled={!canReview}
                  placeholder={uiText("Кратко зафиксируйте вывод по анкете организации")}
                />
              </div>
              {canReview ? (
                <div className="workspace-chip-row">
                  <button type="button" className="btn btn-primary" onClick={review} disabled={busy}>
                    <ShieldCheck size={15} /> {busy ? uiText("Сохраняем…") : uiText("Отметить проверенным")}
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">{uiText("Ответы анкеты")}</div>
              <Badge variant="neutral">{questions.length}</Badge>
            </div>
            <div className="card-body workspace-list">
              {Object.entries(groupedQuestions).map(([block, blockQuestions]) => (
                <div key={block} className="workspace-module-detail">
                  <div className="workspace-section-label">{BLOCK_LABELS[block] || block}</div>
                  <div className="workspace-list" style={{ marginTop: 10 }}>
                    {blockQuestions.map((question) => {
                      const answer = check.answers?.[question.id];
                      const docs = docsByQuestion[String(question.id).replace('q', '')] || [];
                      const comment = answer && typeof answer === 'object' ? answer.comment : '';
                      return (
                        <div key={question.id} className="workspace-list-item" style={{ alignItems: 'flex-start' }}>
                          <div style={{ minWidth: 0 }}>
                            <div className="workspace-item-title">{question.title || question.id}</div>
                            {comment ? <div className="workspace-muted" style={{ marginTop: 4 }}>{uiText("Комментарий: ")}{comment}</div> : null}
                            {docs.length ? (
                              <div className="workspace-chip-row" style={{ marginTop: 8 }}>
                                {docs.map((doc) => (
                                  <span key={doc.id} className="workspace-date-pill">
                                    <FileText size={14} /> {doc.original_name || uiText("Документ #{v0}", {v0: (doc.id)})}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                          </div>
                          <Badge variant={answerVariant(answer)}>
                            {answerVariant(answer) === 'success' ? <CheckCircle2 size={13} /> : answerVariant(answer) === 'danger' ? <AlertCircle size={13} /> : null}
                            {answerLabel(answer)}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
